'use client';

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import {
  useDashboardFilters,
  useDashboardVisibility,
  useDashboardRealTime,
  type UseDashboardFiltersReturn,
  type UseDashboardVisibilityReturn,
} from '../hooks';
import type { FilterState, RealTimeConfig, CardId } from '../types';
import type ITimeZone from '@/src/components/schedule/types/timezone';
import useGetFilterStateOptions, { type UseGetFilterStateOptionsType } from '@/src/views/reports/drilldown/hooks/use-get-filter-options';

/**
 * DashboardContextValue - Combined context type for all dashboard state
 *
 * This context provides shared state across all dashboard sub-routes,
 * enabling the layout-based architecture where each tab is a separate page
 * but shares common state (filters, visibility, real-time config).
 */
interface DashboardContextValue {
  // ─── Routing ───
  /** Current active tab derived from pathname */
  activeTab: string;

  // ─── Filters (useDashboardFilters) ───
  /** Current filter state */
  filters: FilterState;
  /** Set filters (full replace or updater function) */
  setFilters: UseDashboardFiltersReturn['setFilters'];
  /** Handle filter changes from filter panel */
  handleFilterChange: UseDashboardFiltersReturn['handleFilterChange'];
  /** Force reload by updating reloadTimestamp */
  handleReload: UseDashboardFiltersReturn['handleReload'];
  /** Count of active filters (for badge display) */
  activeFiltersCount: number;
  /** Whether the current date range is today */
  isDateRangeToday: boolean;
  /** User's timezone */
  userTimezone: ITimeZone;

  // ─── Visibility (useDashboardVisibility) ───
  /** Raw visibility state object */
  visibility: UseDashboardVisibilityReturn['visibility'];
  /** Card visibility mapped by CardId */
  cardVisibility: Record<CardId, boolean>;
  /** Card ordering array */
  controlOrder: CardId[];
  /** Update card order */
  setControlOrder: (order: CardId[]) => void;
  /** Toggle a visibility setting */
  toggleVisibility: (key: keyof UseDashboardVisibilityReturn['visibility']) => void;
  /** Set a visibility setting */
  setVisibility: (key: keyof UseDashboardVisibilityReturn['visibility'], value: boolean) => void;
  /** Set all cards visible/hidden */
  setAllVisible: (visible: boolean) => void;
  /** Reset visibility and order to defaults */
  resetVisibility: () => void;
  /** Individual visibility getters for backward compatibility */
  showPerformanceChart: boolean;
  showMetricCards: boolean;
  showTrafficSource: boolean;
  showCategories: boolean;
  showGeoMap: boolean;
  showDevices: boolean;
  showOffers: boolean;
  showLandingPages: boolean;
  showSuspicious: boolean;
  showMediaBuyers: boolean;
  showCampaignList: boolean;
  showProfitabilityCards: boolean;
  showRecommendations: boolean;
  isCompactMetrics: boolean;
  /** Individual visibility setters for backward compatibility */
  setShowPerformanceChart: (v: boolean) => void;
  setShowMetricCards: (v: boolean) => void;
  setShowTrafficSource: (v: boolean) => void;
  setShowCategories: (v: boolean) => void;
  setShowGeoMap: (v: boolean) => void;
  setShowDevices: (v: boolean) => void;
  setShowOffers: (v: boolean) => void;
  setShowLandingPages: (v: boolean) => void;
  setShowSuspicious: (v: boolean) => void;
  setShowMediaBuyers: (v: boolean) => void;
  setShowCampaignList: (v: boolean) => void;
  setShowProfitabilityCards: (v: boolean) => void;
  setShowRecommendations: (v: boolean) => void;
  setIsCompactMetrics: (v: boolean) => void;

  // ─── Real-Time (useDashboardRealTime) ───
  /** Real-time configuration */
  realTimeConfig: RealTimeConfig;
  /** Set real-time configuration */
  setRealTimeConfig: (config: RealTimeConfig | ((prev: RealTimeConfig) => RealTimeConfig)) => void;
  /** Toggle real-time on/off */
  toggleRealTime: () => void;
  /** Set polling interval (5 or 10 minutes) */
  setRealTimeInterval: (minutes: number | null) => void;
  /** Whether real-time can be enabled (date is today) */
  canEnableRealTime: boolean;

  // ─── Filter Panel State ───
  /** Whether the filter panel is expanded */
  isFilterPanelExpanded: boolean;
  /** Set filter panel expanded state */
  setIsFilterPanelExpanded: (expanded: boolean) => void;

  // ─── Filter Options (useGetFilterStateOptions) ───
  /** Filter options for dropdowns (campaigns, traffic sources, etc.) */
  filterOptions: UseGetFilterStateOptionsType['options'];
  /** Loading states for filter options */
  filterOptionsLoading: UseGetFilterStateOptionsType['loaders'];
  /** Raw filter data (rotations, campaigns, etc. as returned by APIs) */
  filterData: UseGetFilterStateOptionsType['data'];
}

const DashboardContext = createContext<DashboardContextValue | null>(null);

/**
 * DashboardProvider - Provides shared dashboard state to all child components
 *
 * This provider wraps the dashboard layout and makes filter, visibility,
 * and real-time state available to all dashboard pages.
 *
 * Usage:
 * - Wrap dashboard layout with <DashboardProvider>
 * - Access state in any dashboard page with useDashboard()
 */
export function DashboardProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();

  // Filter panel expanded state
  const [isFilterPanelExpanded, setIsFilterPanelExpanded] = useState(false);

  // Derive activeTab from pathname: /dashboard/rotations → 'rotations'
  const activeTab = useMemo(() => {
    if (pathname === '/dashboard' || pathname === '/dashboard/') {
      return 'overview';
    }
    // Extract first path segment after /dashboard/
    const segments = pathname.replace('/dashboard/', '').split('/');
    return segments[0] || 'overview';
  }, [pathname]);

  // Initialize hooks
  const filtersHook = useDashboardFilters();
  const visibilityHook = useDashboardVisibility();
  const realTimeHook = useDashboardRealTime(
    filtersHook.isDateRangeToday,
    filtersHook.handleReload
  );
  const filterOptionsHook = useGetFilterStateOptions();

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo<DashboardContextValue>(() => ({
    // Routing
    activeTab,

    // Filters
    filters: filtersHook.filters,
    setFilters: filtersHook.setFilters,
    handleFilterChange: filtersHook.handleFilterChange,
    handleReload: filtersHook.handleReload,
    activeFiltersCount: filtersHook.activeFiltersCount,
    isDateRangeToday: filtersHook.isDateRangeToday,
    userTimezone: filtersHook.userTimezone,

    // Visibility
    visibility: visibilityHook.visibility,
    cardVisibility: visibilityHook.cardVisibility,
    controlOrder: visibilityHook.controlOrder,
    setControlOrder: visibilityHook.setControlOrder,
    toggleVisibility: visibilityHook.toggle,
    setVisibility: visibilityHook.set,
    setAllVisible: visibilityHook.setAllVisible,
    resetVisibility: visibilityHook.reset,
    // Individual getters
    showPerformanceChart: visibilityHook.showPerformanceChart,
    showMetricCards: visibilityHook.showMetricCards,
    showTrafficSource: visibilityHook.showTrafficSource,
    showCategories: visibilityHook.showCategories,
    showGeoMap: visibilityHook.showGeoMap,
    showDevices: visibilityHook.showDevices,
    showOffers: visibilityHook.showOffers,
    showLandingPages: visibilityHook.showLandingPages,
    showSuspicious: visibilityHook.showSuspicious,
    showMediaBuyers: visibilityHook.showMediaBuyers,
    showCampaignList: visibilityHook.showCampaignList,
    showProfitabilityCards: visibilityHook.showProfitabilityCards,
    showRecommendations: visibilityHook.showRecommendations,
    isCompactMetrics: visibilityHook.isCompactMetrics,
    // Individual setters
    setShowPerformanceChart: visibilityHook.setShowPerformanceChart,
    setShowMetricCards: visibilityHook.setShowMetricCards,
    setShowTrafficSource: visibilityHook.setShowTrafficSource,
    setShowCategories: visibilityHook.setShowCategories,
    setShowGeoMap: visibilityHook.setShowGeoMap,
    setShowDevices: visibilityHook.setShowDevices,
    setShowOffers: visibilityHook.setShowOffers,
    setShowLandingPages: visibilityHook.setShowLandingPages,
    setShowSuspicious: visibilityHook.setShowSuspicious,
    setShowMediaBuyers: visibilityHook.setShowMediaBuyers,
    setShowCampaignList: visibilityHook.setShowCampaignList,
    setShowProfitabilityCards: visibilityHook.setShowProfitabilityCards,
    setShowRecommendations: visibilityHook.setShowRecommendations,
    setIsCompactMetrics: visibilityHook.setIsCompactMetrics,

    // Real-time
    realTimeConfig: realTimeHook.config,
    setRealTimeConfig: realTimeHook.setConfig,
    toggleRealTime: realTimeHook.toggleRealTime,
    setRealTimeInterval: realTimeHook.setInterval,
    canEnableRealTime: realTimeHook.canEnableRealTime,

    // Filter panel state
    isFilterPanelExpanded,
    setIsFilterPanelExpanded,

    // Filter options
    filterOptions: filterOptionsHook.options,
    filterOptionsLoading: filterOptionsHook.loaders,
    filterData: filterOptionsHook.data,
  }), [
    activeTab,
    filtersHook,
    visibilityHook,
    realTimeHook,
    isFilterPanelExpanded,
    filterOptionsHook.options,
    filterOptionsHook.loaders,
    filterOptionsHook.data,
  ]);

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
}

/**
 * useDashboard - Access dashboard context in any dashboard page
 *
 * Must be used within a DashboardProvider (dashboard layout provides this).
 *
 * @throws Error if used outside DashboardProvider
 *
 * @example
 * ```tsx
 * function RotationsPage() {
 *   const { filters, realTimeConfig } = useDashboard();
 *   return <RotationList filters={filters} realTimeConfig={realTimeConfig} />;
 * }
 * ```
 */
export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}

// Re-export types for convenience
export type { DashboardContextValue };
